/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import { IonItem, IonLabel } from "@ionic/react";
import React, { useEffect, useMemo, useState } from "react";
import { AnswerFormats } from "../../models/enumerations/answer-formats";
import { GroupIds } from "../../models/enumerations/group-ids";
import { SlideType } from "../../models/enumerations/slide-types";
import { Question } from "../../models/interfaces/questions/question";
import { QuestionGroup } from "../../models/interfaces/questions/question-group";
import { QuestionResponse } from "../../models/interfaces/questions/question-response";
import useLinkAccountSetupQuestions from "../../utilities/hooks/use-account-setup-questions";
import ScreenSlide from "../screen-slide/screen-slide";
import { User } from "../../models/interfaces/user";
import BaseCardLayout from "../base-card-layout/base-card-layout";
import LinkMovementScreen from "../link-movement-screen/link-movement-screen";
import AssessmentSettings from "../../models/interfaces/assessment-settings";
import { AssessmentUtils } from "../../utilities/assessments/assessment-utils";
import { Utils } from "../../utilities/utils";
import { useTranslation } from 'react-i18next';
import i18n from "../../i18n";
import { BiologicalSexValue } from "../../models/biological-sex";
import { useBackController } from "../back-controller/back-controller";
import { useNavigationDirection } from "../navigation-direction-context/navigation-direction-content";

const COMPONENT_CLASS = "c-account-setup-screen";

const setupQuestions: QuestionGroup[] = [
    {
        groupId: GroupIds.SETUP,
        groupSort: 0,
        questions: [
            {
                questionTitleId: "biological-sex-question",
                questionId: "biologicalSex",
                questionText: 'account_setup_screen.bioSex_question',
                sort: 1,
                answerFormat: AnswerFormats.OPTIONS,
                answerDetailsId: "biological-sex-info-text",
                answerDetails: 'account_setup_screen.bioSex_details',
                answerOptions: [
                    {
                        id: "biological-sex-female-button",
                        answerId: "FEMALE",
                        answerText: i18n.t('general.female'),
                    },
                    {
                        id: "biological-sex-male-button",
                        answerId: "MALE",
                        answerText: i18n.t('general.male'),
                    },
                ],
            },
            {
                questionId: "birthdate",
                questionText: 'account_setup_screen.birthdate',
                sort: 2,
                answerFormat: AnswerFormats.DATE,
            },
        ],
    },
];

export interface LinkAccountSetupScreenProps {
    user: User;
    linkId: string;
}

const LinkAccountSetupScreen: React.FC<LinkAccountSetupScreenProps> = (props) => {
    const questionGroups = useMemo<QuestionGroup[]>(() => setupQuestions, []);
    const { setDirection } = useNavigationDirection();
    const { invoke } = useBackController();

    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [questionResponses, setQuestionResponses] = useState<
        QuestionResponse[]
    >([]);
    const slides = useLinkAccountSetupQuestions(questionGroups);
    const [isCompleted, setIsCompleted] = useState(false);
    const [settings, setSettings] = useState<AssessmentSettings>({
        completedGroupIds: [],
        currentSlideIndex: 0,
        currentGroupIndex: 0,
        questionResponses: [],
        slideGroups: [],
    });
    const { t } = useTranslation();

    const [updatedUser, setUpdatedUser] = useState(props.user);

    useEffect(() => {
        document.body.classList.add("-nav-hidden");

        return () => {
            document.body.classList.remove("-nav-hidden");
        };
    }, []);

    useEffect(() => {
        const slide = slides[currentSlideIndex];
        if (!slide?.questions) {
            return;
        }
        const question = slide.questions![0];
        if (question.questionId === "biologicalSex") {
            getNextPage();
        }
    }, [questionResponses]);

    const getNextPage = () => {
        var slide = slides[currentSlideIndex];
        if (slide.slideType === SlideType.Question && slide.questions) {
            for (const question of slide.questions) {
                const questionResponse = questionResponses.find(
                    (x) => x.questionId === question.questionId
                );

                // validate has answer
                if (questionResponse == null) {
                    return;
                }
                if (
                    questionResponse.answerId == null &&
                    questionResponse.answerResponse == null
                ) {
                    return;
                }
                if (
                    questionResponse.answerResponse != null &&
                    questionResponse.answerResponse.length === 0
                ) {
                    return;
                }
            }
        }
        setCurrentSlideIndex(currentSlideIndex + 1);
    };

    const submitProfile = async () => {
        let updatedUserTemp = updatedUser;

        questionResponses.forEach((response) => {
            switch (response.questionId) {
                case "biologicalSex":
                    if (!response.answerId) {
                        // could throw error just in case for user. This should never be undefined though.
                        return;
                    }
                    updatedUserTemp.biologicalSex = BiologicalSexValue[`${response.answerId!.charAt(0).toUpperCase()}${response.answerId.slice(1).toLowerCase()}` as BiologicalSexValue];
                    break;

                case "birthdate":
                    updatedUserTemp.birthdate = response.answerResponse ? Utils.convertToISO(response.answerResponse) : response.answerResponse;
                    break;
            }
        });
        if (updatedUserTemp.birthdate) {
            setUpdatedUser(updatedUserTemp);
            setIsCompleted(true);
        }
    };

    const recordAnswer = (selectedAnswer: string, question: Question) => {
        if (
            questionResponses.find((x) => x.questionId === question.questionId)
        ) {
            // if response already exists
            const editingIndex = questionResponses.findIndex(
                (x) => x.questionId === question.questionId
            );
            questionResponses[editingIndex].answerResponse = selectedAnswer;
            setQuestionResponses(questionResponses);
        } else {
            // response does not already exist
            const selectedAnswerValue =
                question.answerOptions?.find(
                    (x) => x.answerId === selectedAnswer
                )!.answerText ?? selectedAnswer;
            const answer = {
                categoryId: questionGroups[0].categoryId!,
                groupId: questionGroups[0].groupId,
                answerId: selectedAnswer,
                questionId: question.questionId,
                answerResponse: selectedAnswerValue,
            };
            setQuestionResponses([...questionResponses, answer]);
        }
    };

    const onBackButtonClick = () => {
        // 1) Offer back to deepest child (MultiplePainScale etc.)
        if (invoke()) return;

        // 2) Mark navigation direction as backward
        setDirection("backward");

        if (AssessmentUtils.isFirstSlideAndFirstGroup(settings)) {
            setIsCompleted(false);
        }

        setSettings(AssessmentUtils.moveToPreviousPage(settings));
    };

    return (
        <>
            {
                // if
                isCompleted && (
                    <LinkMovementScreen
                        linkId={props.linkId}
                        screenSettings={settings}
                        setScreenSettings={setSettings}
                        settingsOnBack={onBackButtonClick}
                        user={updatedUser}
                    />
                )
            }
            {
                // if
                !isCompleted && (
                    <BaseCardLayout>
                        <div style={{ width: "100%" }}>
                            <div className={`${COMPONENT_CLASS} h-full`}>
                                {
                                    // if
                                    questionGroups && (
                                        <div
                                            className={`${COMPONENT_CLASS}__content h-full ${currentSlideIndex + 1 === slides.length
                                                ? "has-button"
                                                : ""
                                                }`}>
                                            {
                                                // if
                                                slides[currentSlideIndex] && (
                                                    <ScreenSlide
                                                        recordedAnswers={questionResponses}
                                                        recordAnswer={recordAnswer}
                                                        page={slides[currentSlideIndex]}
                                                        setupSubmit={submitProfile}
                                                    />
                                                )
                                            }
                                        </div>
                                    )
                                }
                                {
                                    // if
                                    !questionGroups && (
                                        <IonItem>
                                            <IonLabel>
                                                {t('account_setup_screen.loadingQuestions')}
                                            </IonLabel>
                                        </IonItem>
                                    )
                                }
                            </div>
                        </div>
                    </BaseCardLayout>
                )
            }

        </>
    );
};

export default LinkAccountSetupScreen;
